import React, { useEffect, useRef } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import TableCreatorTableTitle from "@FillInTableContainer/shared/components/TableCreatorTableTitle/TableCreatorTableTitle";
import { TableCreatorGroupShellSectionList } from "@FillInTableContainer/shared/types/tableCreator.types";
import { getTableTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { padStart } from "@utils/padStart/padStart";

import DealLaScFinancialTermsGroupShellSection from "./components/DealLaScFinancialTermsGroupShellSection/DealLaScFinancialTermsGroupShellSection";
import * as S from "./DealLaScFinancialTermsTable.style";
import { useHandleDealLaScFinancialTermsTableCreatorTable } from "./hooks/useHandleDealLaScFinancialTermsTableCreatorTable";

export interface TableInfoProps {
  groupTableKey: DealLaScSection;
  tableKey: string | null;
  groupShellSectionList: TableCreatorGroupShellSectionList;
  groupTableIndex: number;
  tableIndex: number;
  tableCount: number;
}
type Props = TableInfoProps & {
  customTableDeleteEvent?: () => void;
};

const DealLaScFinancialTermsTable = ({
  customTableDeleteEvent,
  groupShellSectionList,
  groupTableIndex,
  groupTableKey,
  tableCount,
  tableIndex,
  tableKey,
}: Props) => {
  const tableTitleRef = useRef<HTMLDivElement>(null);
  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;

  const tablePropsInfo: TableInfoProps = {
    groupTableKey,
    tableKey,
    groupTableIndex,
    tableIndex,
    tableCount,
    groupShellSectionList,
  };

  const {
    focusStatus,
    isFocused,
    handleClickDeleteButton,
    handleChangeDefaultFocus,
    focusEventToChangePositionInfo,
  } = useHandleDealLaScFinancialTermsTableCreatorTable(tablePropsInfo);

  const isClearTable =
    isFocused &&
    focusStatus.container === "table" &&
    focusStatus.type === "clear";

  const isDeleteTable =
    isFocused &&
    focusStatus.container === "table" &&
    focusStatus.type === "delete";

  const title =
    tableCount > 1
      ? `${getTableTitle(articleSection, tableKey)} ${padStart(tableIndex + 1)}`
      : getTableTitle(articleSection, tableKey) || "";

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;

        const clickSelectButton = tableTitleRef.current?.contains(target);

        if (clickSelectButton) return;
        if (target.dataset.button) return;

        handleChangeDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleChangeDefaultFocus, isFocused]);

  const handleFocusTableTitle = () => {
    focusEventToChangePositionInfo({
      type: "clear",
      container: "table",
    });
  };

  return (
    <>
      <S.TableWrapper
        isFocused={isFocused}
        isClearTable={isClearTable}
        isDeleteTable={isDeleteTable}
        isDraggingTable={isFocused && focusStatus.type === "dragging"}
      >
        {(isClearTable || isDeleteTable) && (
          <S.DeleteTableButtonWrapper>
            <S.DeleteTableButton
              data-button="delete-button"
              onClick={() => {
                handleClickDeleteButton();
                if (focusStatus.type === "delete" && customTableDeleteEvent) {
                  customTableDeleteEvent();
                }
              }}
            >
              <img src={IconXMark} alt="button for clear table" />
              {focusStatus.type === "clear" ? `Clear ${title} Table` : null}
              {focusStatus.type === "delete" ? `Delete ${title} Table` : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        )}
        <div ref={tableTitleRef}>
          {getTableTitle(articleSection, tableKey) && (
            <TableCreatorTableTitle
              tableKey={tableKey}
              groupTableKey={groupTableKey}
              groupTableIndex={groupTableIndex}
              tableIndex={tableIndex}
              isDragging={isFocused && focusStatus.type === "dragging"}
              onClickHandler={handleFocusTableTitle}
              tableTitle={title}
              titleWrapperColor="#F8F9FA"
            />
          )}
        </div>
        {groupShellSectionList.map(
          (
            { groupShellSectionKey, groupShellList },
            groupShellSectionIndex,
          ) => (
            <DealLaScFinancialTermsGroupShellSection
              key={`group-shell-${String(groupTableIndex)}-${String(
                tableIndex,
              )}-${String(groupShellSectionIndex)}`}
              groupShellList={groupShellList}
              groupTableKey={groupTableKey}
              groupTableIndex={groupTableIndex}
              tableKey={tableKey}
              tableIndex={tableIndex}
              groupShellSectionKey={groupShellSectionKey}
              groupShellSectionIndex={groupShellSectionIndex}
            />
          ),
        )}
      </S.TableWrapper>
    </>
  );
};

export default DealLaScFinancialTermsTable;
